/**
 * WeekNavigator component
 * Navigation controls for moving between weeks
 */

import { useTranslation } from 'react-i18next';
import { formatDateForDisplay, getWeekStart, getWeekEnd } from '@/lib/utils/date';

interface WeekNavigatorProps {
  currentWeekStart: Date;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
}

/**
 * WeekNavigator component
 *
 * Features:
 * - Previous/Next week buttons
 * - "Today" button to jump to current week
 * - Display current week range
 *
 * @example
 * ```tsx
 * <WeekNavigator
 *   currentWeekStart={weekStart}
 *   onPreviousWeek={() => setWeekStart(getPreviousWeek(weekStart))}
 *   onNextWeek={() => setWeekStart(getNextWeek(weekStart))}
 *   onToday={() => setWeekStart(getWeekStart(new Date()))}
 * />
 * ```
 */
export function WeekNavigator({
  currentWeekStart,
  onPreviousWeek,
  onNextWeek,
  onToday,
}: WeekNavigatorProps) {
  const { t } = useTranslation();
  const weekEnd = getWeekEnd(currentWeekStart);
  const isCurrentWeek =
    getWeekStart(new Date()).toDateString() === currentWeekStart.toDateString();

  return (
    <div className="flex items-center justify-between mb-6">
      {/* Week range display */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          {formatDateForDisplay(currentWeekStart)} -{' '}
          {formatDateForDisplay(weekEnd)}
        </h2>
        {isCurrentWeek && (
          <p className="text-sm text-primary-600 font-medium">{t('availability.thisWeek')}</p>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPreviousWeek}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label={t('availability.previousWeek')}
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {!isCurrentWeek && (
          <button
            type="button"
            onClick={onToday}
            className="px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
          >
            {t('availability.today')}
          </button>
        )}

        <button
          type="button"
          onClick={onNextWeek}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label={t('availability.nextWeek')}
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
